export type OperatorConfig = {
  label: string
  value: string
}

export type LabelValue = {
  label: string
  value: string
}

export type FieldConfig = {
  /** Human-friendly label shown in the dropdown */
  label: string
  /** The JS path used in the expression, e.g. `$pet.attr.primary_color` */
  value: string
  /** Group label used to cluster fields under a `Select.OptGroup` in the dropdown */
  group: string
  /** Optional curated list of allowed values; renders a value dropdown when present */
  options?: LabelValue[]
  /** Optional per-field operator list; overrides the editor's default operators */
  operators?: OperatorConfig[]
}

/**
 * Everything the editor needs to be configured: the selectable fields and the
 * default operator list. Pass a custom one to `<DevGuiPage config={...} />` to
 * reuse the editor elsewhere; otherwise `DEFAULT_CONFIG` is used.
 */
export type ExpressionEditorConfig = {
  fields: FieldConfig[]
  operators: OperatorConfig[]
}

/** Equality-only operators — handy for enum-like fields */
export const EQUALITY_OPERATORS: OperatorConfig[] = [
  { value: '===', label: 'equal (===)' },
  { value: '!==', label: 'not equal (!==)' }
]

/** Full default operator set (equality + ordering) */
export const COMPARISON_OPERATORS: OperatorConfig[] = [
  ...EQUALITY_OPERATORS,
  { value: '>', label: 'greater than (>)' },
  { value: '<', label: 'less than (<)' },
  { value: '>=', label: 'greater or equal (>=)' },
  { value: '<=', label: 'less or equal (<=)' }
]

/**
 * Default list of fields selectable on the left-hand side of a condition.
 * Each field may carry its own `options` (value dropdown) and `operators`.
 */
export const KNOWN_FIELDS: FieldConfig[] = []

/** The configuration used when no custom one is provided. */
export const DEFAULT_CONFIG: ExpressionEditorConfig = {
  fields: KNOWN_FIELDS,
  operators: COMPARISON_OPERATORS
}

export const findField = (fields: FieldConfig[], path: string): FieldConfig | undefined =>
  fields.find(f => f.value === path)

/** Operators available for a field: its own override, or the editor default. */
export const operatorsForField = (
  config: ExpressionEditorConfig,
  field?: FieldConfig
): OperatorConfig[] => (field && field.operators ? field.operators : config.operators)

/** Cluster fields by their `group`, preserving first-seen group order. */
export const groupFields = (
  fields: FieldConfig[]
): Array<{ group: string; fields: FieldConfig[] }> =>
  fields.reduce((acc: Array<{ group: string; fields: FieldConfig[] }>, field) => {
    const existing = acc.find(g => g.group === field.group)
    if (existing) {
      existing.fields.push(field)
    } else {
      acc.push({ group: field.group, fields: [field] })
    }
    return acc
  }, [])
